import { type ReactNode, useCallback, useRef, useState } from "react"

import { Boton } from "./Boton"
import { Modal } from "./Modal"

export type PedidoConfirmacion = {
  titulo: string
  mensaje: ReactNode
  /** El rótulo del botón que sigue. Por omisión «Seguir». */
  aceptar?: string
  /** Para lo que borra o no se deshace: el botón va en `peligro`. */
  peligro?: boolean
}

/**
 * Una confirmación con la ventana del sistema, en lugar de `window.confirm`.
 *
 * ⚠ **`window.confirm` no se usa** (10/10): lo pinta el navegador, con sus colores y su
 * «app.setiecadesk.com dice», y se lee como un aviso de fuera del sistema. Esto usa el
 * mismo `Modal` que el resto, con su foco, su Esc y su lectura para lectores de pantalla.
 *
 * Uso: `const [confirmacion, confirmar] = useConfirmar()`; se pinta `{confirmacion}` y
 * `if (await confirmar({ titulo, mensaje })) …`. Cerrar con Esc o fuera es «no».
 */
export function useConfirmar(): [ReactNode, (pedido: PedidoConfirmacion) => Promise<boolean>] {
  const [pedido, setPedido] = useState<PedidoConfirmacion | null>(null)
  const resolver = useRef<((si: boolean) => void) | null>(null)

  const confirmar = useCallback(
    (p: PedidoConfirmacion) =>
      new Promise<boolean>((resolve) => {
        // Una pregunta nueva contesta «no» a la que quedara abierta: nadie espera dos.
        resolver.current?.(false)
        resolver.current = resolve
        setPedido(p)
      }),
    [],
  )

  function responder(si: boolean) {
    resolver.current?.(si)
    resolver.current = null
    setPedido(null)
  }

  const ventana = (
    <Modal
      abierto={pedido !== null}
      titulo={pedido?.titulo ?? ""}
      onCerrar={() => responder(false)}
      ancho="sm"
    >
      <div className="text-sm">{pedido?.mensaje}</div>
      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={() => responder(false)}>
          Cancelar
        </Boton>
        <Boton variante={pedido?.peligro ? "peligro" : "primario"} onClick={() => responder(true)}>
          {pedido?.aceptar ?? "Seguir"}
        </Boton>
      </div>
    </Modal>
  )

  return [ventana, confirmar]
}
